import type { ReactElement } from "react";
import { Plus, Search } from "lucide-react";
import {
  EXPOSURE_RISK_CATEGORIES,
  EXPOSURE_RISK_CRITICALITIES,
  EXPOSURE_RISK_TYPES,
  type ExposureRiskCandidate,
  type ExposureRiskCategory,
  type ExposureRiskCriticality,
  type ExposureRiskType,
} from "@playbookai/widget-schemas";
import { Button } from "../../atoms/Button/Button.js";
import { Input } from "../../atoms/Input/Input.js";
import { Select } from "../../molecules/Select/Select.js";
import { RiskRowSummary } from "./Component.js";
import { cn } from "../../lib/utils.js";
import {
  EXPOSURE_RISK_CATEGORY_LABELS,
  EXPOSURE_RISK_CRITICALITY_LABELS,
  EXPOSURE_RISK_TYPE_LABELS,
} from "./presentation.js";

export interface RiskFilters {
  readonly search: string;
  readonly type: ExposureRiskType | "all";
  readonly category: ExposureRiskCategory | "all";
  readonly criticality: ExposureRiskCriticality | "all";
}

/**
 * Master column of the expanded editor. Search, filters and selection are
 * presentation state owned by the parent — none of it is persisted.
 */
export function RiskList({
  risks,
  total,
  filters,
  selectedId,
  readOnly,
  canAdd,
  onFiltersChange,
  onSelect,
  onAdd,
}: {
  risks: readonly ExposureRiskCandidate[];
  total: number;
  filters: RiskFilters;
  selectedId: string | undefined;
  readOnly: boolean;
  canAdd: boolean;
  onFiltersChange: (next: RiskFilters) => void;
  onSelect: (riskId: string) => void;
  onAdd: () => void;
}): ReactElement {
  return (
    <section className="flex min-h-0 min-w-0 flex-col rounded-lg border border-pb-border bg-pb-card-elevated/30">
      <header className="flex flex-col gap-2.5 border-b border-pb-border p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-medium text-pb-text-secondary">
            Candidate risks ({total})
          </h3>
          <Button
            variant="secondary"
            size="sm"
            disabled={readOnly || !canAdd}
            onClick={onAdd}
          >
            <Plus className="h-4 w-4" />
            Add candidate
          </Button>
        </div>
        <Input
          aria-label="Search risks"
          placeholder="Search risks"
          value={filters.search}
          prefix={<Search className="h-4 w-4 text-pb-text-muted" />}
          onChange={(event) =>
            onFiltersChange({ ...filters, search: event.target.value })
          }
        />
        <details>
          <summary className="cursor-pointer text-xs text-pb-text-secondary">
            Filters
            {[filters.type, filters.category, filters.criticality].some(
              (value) => value !== "all",
            )
              ? " · Active"
              : ""}
          </summary>
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Select
              label="Filter: Type"
              value={filters.type}
              options={[
                { value: "all", label: "All types" },
                ...EXPOSURE_RISK_TYPES.map((value) => ({
                  value,
                  label: EXPOSURE_RISK_TYPE_LABELS[value],
                })),
              ]}
              onChange={(value) =>
                onFiltersChange({
                  ...filters,
                  type: value as ExposureRiskType | "all",
                })
              }
            />
            <Select
              label="Filter: Category"
              value={filters.category}
              options={[
                { value: "all", label: "All categories" },
                ...EXPOSURE_RISK_CATEGORIES.map((value) => ({
                  value,
                  label: EXPOSURE_RISK_CATEGORY_LABELS[value],
                })),
              ]}
              onChange={(value) =>
                onFiltersChange({
                  ...filters,
                  category: value as ExposureRiskCategory | "all",
                })
              }
            />
            <Select
              label="Filter: Criticality"
              value={filters.criticality}
              options={[
                { value: "all", label: "All criticalities" },
                ...EXPOSURE_RISK_CRITICALITIES.map((value) => ({
                  value,
                  label: EXPOSURE_RISK_CRITICALITY_LABELS[value],
                })),
              ]}
              onChange={(value) =>
                onFiltersChange({
                  ...filters,
                  criticality: value as ExposureRiskCriticality | "all",
                })
              }
            />
          </div>
        </details>
      </header>
      {risks.length === 0 ? (
        <p className="px-3 py-8 text-center text-xs italic text-pb-text-muted">
          {total === 0
            ? "No candidate risks yet. Add the first exposure, containment-failure or side-effect risk."
            : "No candidate risks match this search and filter combination."}
        </p>
      ) : (
        <ul
          aria-label="Candidate risks"
          className="min-h-0 max-h-120 flex-1 divide-y divide-pb-border overflow-y-auto"
        >
          {risks.map((risk) => (
            <li key={risk.id} className="min-w-0">
              <RiskRowButton
                risk={risk}
                selected={risk.id === selectedId}
                onSelect={() => onSelect(risk.id)}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function RiskRowButton({
  risk,
  selected,
  onSelect,
}: {
  risk: ExposureRiskCandidate;
  selected: boolean;
  onSelect: () => void;
}): ReactElement {
  return (
    // eslint-disable-next-line ds/no-hand-built -- kept: a 12px-inset row with a teal start edge; ListItem has no inline padding and drops the edge, which AP-07's selection fill does not cover.
    <button
      type="button"
      aria-current={selected ? "true" : undefined}
      onClick={onSelect}
      className={cn(
        "block w-full min-w-0 border-s-2 border-transparent px-3 py-3 text-start hover:pb-overlay-paint",
        selected && "border-s-2 border-pb-teal bg-pb-teal/10",
      )}
    >
      <RiskRowSummary risk={risk} />
    </button>
  );
}
